/**
 * BuyISDDialog — Purchase $ISD with stablecoins.
 *
 * Calculator-style layout: enter USD amount, see real-time ISD estimate,
 * select chain/stablecoin, confirm purchase.
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import type { MMOMonetizationManager } from '../../managers/MMOMonetizationManager';
import type { Chain, Stablecoin } from '../../shared/common/MonetizationTypes';
import { Colors, CSSColors, createDialogChrome, closeDialog, Fonts, FontSize, Radius } from './GameUITheme';
import { PaymentSelectionPanel } from './PaymentSelectionPanel';
import { PaymentFlowManager } from '../../managers/PaymentFlowManager';
import { getApiBaseUrl } from '../../utils/ApiConfig';
import { isMobile } from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';

/* ── Layout ──────────────────────────────────────────────── */
const PAD      = 20;

const API_URL = getApiBaseUrl();

/* ── Preset USD amounts ──────────────────────────────────── */
const PRESETS = [1, 10, 25, 50, 100, 250, 500];
const MIN_USD = 1;
const MAX_USD = 10000;

export class BuyISDDialog extends Phaser.GameObjects.Container {
  private innerW: number;
  private monetization: MMOMonetizationManager;

  /* State */
  private amountUsd = 100;
  private isdEstimate = 0;
  private pricePerIsd = 0;
  private priceSource = 'loading';
  private isPurchasing = false;
  private quoteDebounce: ReturnType<typeof setTimeout> | null = null;

  /* UI refs */
  private contentContainer!: Phaser.GameObjects.Container;
  private paymentPanel: PaymentSelectionPanel | null = null;
  private statusText!: Phaser.GameObjects.Text;
  private amountInputDom: Phaser.GameObjects.DOMElement | null = null;

  constructor(scene: Phaser.Scene) {
    const mobile = isMobile(scene);
    const DIALOG_W = mobile ? 340 : 440;
    const DIALOG_H = 540;

    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - DIALOG_W / 2, height / 2 - DIALOG_H / 2);

    this.innerW = DIALOG_W - PAD * 2;

    this.monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);

    createDialogChrome(scene, this, DIALOG_W, DIALOG_H, {
      title: I18n.t('dialog.buy_isd'),
      onClose: () => this.close(),
    });

    /* Content area */
    this.contentContainer = scene.add.container(PAD, 55);
    this.add(this.contentContainer);

    /* Status text at bottom */
    this.statusText = scene.add.text(DIALOG_W / 2, DIALOG_H - 18, '', {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
      wordWrap: { width: DIALOG_W - 40 },
    }).setOrigin(0.5, 0.5);
    this.add(this.statusText);

    this.setDepth(2000);
    scene.add.existing(this);

    this.fetchQuote();
    this.renderContent();
  }

  private close(): void { closeDialog(this.scene, this); }

  /* ================================================================
     QUOTE
     ================================================================ */

  private async fetchQuote(): Promise<void> {
    try {
      const response = await fetch(`${API_URL}/purchase/isd-quote?amountUsd=${this.amountUsd}`);
      if (!response.ok) throw new Error(`Quote failed: ${response.status}`);
      const data = await response.json();
      this.isdEstimate = data.isdAmount || 0;
      this.pricePerIsd = data.pricePerIsd || 0;
      this.priceSource = data.source || 'default';
    } catch {
      this.priceSource = 'error';
      this.isdEstimate = 0;
    }
    // Don't re-render if the user is mid-typing in the amount input — would steal focus
    const inputEl = this.amountInputDom?.node as HTMLInputElement | undefined;
    if (inputEl && document.activeElement === inputEl) return;
    this.renderContent();
  }

  private debouncedFetchQuote(): void {
    if (this.quoteDebounce) clearTimeout(this.quoteDebounce);
    this.quoteDebounce = setTimeout(() => this.fetchQuote(), 300);
  }

  /* ================================================================
     RENDER
     ================================================================ */

  private renderContent(): void {
    this.contentContainer.removeAll(true);
    this.paymentPanel = null;
    let y = 0;

    /* ── Section: You Pay ──────────────────────────────────── */
    this.contentContainer.add(this.scene.add.text(0, y, 'YOU PAY', {
      fontSize: FontSize.xs, color: CSSColors.textMuted, fontFamily: Fonts.heading,
    }));
    y += 20;

    /* Amount input box (editable via DOM input) */
    const amountBg = this.scene.add.graphics();
    amountBg.fillStyle(Colors.bgPanelLit, 1);
    amountBg.fillRoundedRect(0, y, this.innerW, 50, Radius.md);
    amountBg.lineStyle(1, Colors.cyanMuted, 0.4);
    amountBg.strokeRoundedRect(0, y, this.innerW, 50, Radius.md);
    this.contentContainer.add(amountBg);

    const dollarSign = this.scene.add.text(16, y + 25, '$', {
      fontSize: FontSize.xxl, color: CSSColors.textMuted, fontFamily: Fonts.heading,
    }).setOrigin(0, 0.5);
    this.contentContainer.add(dollarSign);

    const usdLabel = this.scene.add.text(this.innerW - 12, y + 25, 'USD', {
      fontSize: FontSize.sm, color: CSSColors.textMuted, fontFamily: Fonts.heading,
    }).setOrigin(1, 0.5);
    this.contentContainer.add(usdLabel);

    const inputEl = document.createElement('input');
    inputEl.type = 'number';
    inputEl.min = String(MIN_USD);
    inputEl.max = String(MAX_USD);
    inputEl.step = '1';
    inputEl.value = String(this.amountUsd);
    inputEl.placeholder = 'Enter amount';
    inputEl.style.cssText = [
      'width:' + (this.innerW - 90) + 'px',
      'height:36px',
      'background:transparent',
      'border:none',
      'outline:none',
      'color:#ffffff',
      'font-family:Orbitron,sans-serif',
      'font-size:24px',
      'font-weight:bold',
      'padding:0',
      'margin:0',
    ].join(';');
    inputEl.addEventListener('input', () => {
      const raw = parseFloat(inputEl.value);
      if (!isNaN(raw) && raw >= MIN_USD && raw <= MAX_USD) {
        this.amountUsd = Math.floor(raw);
        this.debouncedFetchQuote();
        // Don't re-render here — would destroy the input and lose focus
      }
    });
    inputEl.addEventListener('blur', () => {
      // On blur, snap to a valid value and re-render presets to update active state
      const raw = parseFloat(inputEl.value);
      if (isNaN(raw) || raw < MIN_USD) this.amountUsd = MIN_USD;
      else if (raw > MAX_USD) this.amountUsd = MAX_USD;
      else this.amountUsd = Math.floor(raw);
      inputEl.value = String(this.amountUsd);
      this.renderContent();
    });
    this.amountInputDom = this.scene.add.dom(40 + (this.innerW - 90) / 2, y + 25, inputEl);
    this.amountInputDom.setOrigin(0.5, 0.5);
    this.contentContainer.add(this.amountInputDom);

    y += 60;

    /* ── Preset buttons ─────────────────────────────────────── */
    const btnW = Math.floor((this.innerW - (PRESETS.length - 1) * 5) / PRESETS.length);
    let bx = 0;
    for (const amt of PRESETS) {
      const isActive = amt === this.amountUsd;
      const btn = this.createPresetBtn(bx, y, btnW, 30, `$${amt}`, isActive, () => {
        this.amountUsd = amt;
        this.debouncedFetchQuote();
        this.renderContent();
      });
      this.contentContainer.add(btn);
      bx += btnW + 5;
    }
    y += 42;

    /* ── Arrow ──────────────────────────────────────────────── */
    const arrow = this.scene.add.text(this.innerW / 2, y, '\u25BC', {
      fontSize: FontSize.lg, color: CSSColors.cyan, fontFamily: Fonts.body,
    }).setOrigin(0.5, 0);
    this.contentContainer.add(arrow);
    y += 28;

    /* ── Section: You Receive ──────────────────────────────── */
    this.contentContainer.add(this.scene.add.text(0, y, 'YOU RECEIVE', {
      fontSize: FontSize.xs, color: CSSColors.textMuted, fontFamily: Fonts.heading,
    }));
    y += 20;

    /* ISD amount display */
    const isdBg = this.scene.add.graphics();
    isdBg.fillStyle(0x0a1a2a, 1);
    isdBg.fillRoundedRect(0, y, this.innerW, 50, Radius.md);
    isdBg.lineStyle(1, Colors.green, 0.3);
    isdBg.strokeRoundedRect(0, y, this.innerW, 50, Radius.md);
    this.contentContainer.add(isdBg);

    const isdAmountStr = this.priceSource === 'loading'
      ? '...'
      : this.priceSource === 'error'
        ? 'Quote unavailable'
        : this.isdEstimate.toLocaleString();

    const isdText = this.scene.add.text(16, y + 25, isdAmountStr, {
      fontSize: FontSize.xxl, color: CSSColors.green, fontFamily: Fonts.heading,
    }).setOrigin(0, 0.5);
    this.contentContainer.add(isdText);

    const isdLabel = this.scene.add.text(this.innerW - 12, y + 25, '$ISD', {
      fontSize: FontSize.sm, color: CSSColors.textMuted, fontFamily: Fonts.heading,
    }).setOrigin(1, 0.5);
    this.contentContainer.add(isdLabel);

    y += 58;

    /* ── Price info ─────────────────────────────────────────── */
    if (this.pricePerIsd > 0) {
      const rateText = `1 $ISD = $${this.pricePerIsd.toFixed(6)} USD`;
      const sourceTag = this.priceSource === 'jupiter' ? ' (live)' : ' (est.)';
      this.contentContainer.add(this.scene.add.text(this.innerW / 2, y, rateText + sourceTag, {
        fontSize: FontSize.xs, color: CSSColors.textDim, fontFamily: Fonts.body,
      }).setOrigin(0.5, 0));
      y += 22;
    }

    /* ── Payment selection ──────────────────────────────────── */
    this.paymentPanel = new PaymentSelectionPanel(this.scene, 0, y, this.innerW);
    // PaymentSelectionPanel calls scene.add.existing, so just add to our container
    this.contentContainer.add(this.paymentPanel);
    y += this.paymentPanel.getHeight() + 12;

    /* ── Confirm button ─────────────────────────────────────── */
    const confirmLabel = this.isPurchasing
      ? 'Processing...'
      : `Buy ${this.isdEstimate.toLocaleString()} $ISD for $${this.amountUsd}`;

    const confirmBtn = this.createActionBtn(
      0, y, this.innerW, 44,
      confirmLabel,
      this.isPurchasing ? Colors.bgPanelLit : Colors.cyan,
      this.isPurchasing ? CSSColors.textMuted : '#000000',
      () => {
        if (!this.isPurchasing) this.executePurchase();
      },
    );
    this.contentContainer.add(confirmBtn);
    y += 54;

    /* ── Disclaimer ─────────────────────────────────────────── */
    this.contentContainer.add(this.scene.add.text(this.innerW / 2, y, 'ISD is burned on-chain. No ISD tokens leave the game.', {
      fontSize: '11px', color: CSSColors.textDim, fontFamily: Fonts.body,
      wordWrap: { width: this.innerW },
    }).setOrigin(0.5, 0));
  }

  /* ================================================================
     PURCHASE
     ================================================================ */

  private async executePurchase(): Promise<void> {
    if (this.isPurchasing || this.isdEstimate <= 0) return;

    const chain = this.paymentPanel?.getSelectedChain() || 'solana';
    const stablecoin = this.paymentPanel?.getSelectedStablecoin() || 'USDC';

    this.isPurchasing = true;
    this.renderContent();
    this.setStatus(`Purchasing $ISD for $${this.amountUsd} ${stablecoin} on ${chain}...`);

    try {
      const paymentFlow = ServiceLocator.tryGet<PaymentFlowManager>(Services.PAYMENT_FLOW);
      if (paymentFlow) {
        const result = await paymentFlow.executePayment({
          assetType: 'isd' as any,
          amountUsd: this.amountUsd,
          chain: chain as Chain,
          stablecoin: stablecoin as Stablecoin,
          metadata: { isdEstimate: this.isdEstimate },
        });

        this.isPurchasing = false;

        if (result.success) {
          this.setStatus(`$ISD credited to your account!`);
          this.scene.time.delayedCall(2000, () => {
            if (this.scene) this.close();
          });
        } else {
          this.setStatus(result.error || 'Purchase failed');
          this.renderContent();
        }
      } else {
        // Fallback: direct API call for Solana flow
        await this.directSolanaPurchase(stablecoin as Stablecoin);
      }
    } catch (error) {
      this.isPurchasing = false;
      this.setStatus(error instanceof Error ? error.message : 'Purchase failed');
      this.renderContent();
    }
  }

  private async directSolanaPurchase(stablecoin: Stablecoin): Promise<void> {
    // Get wallet manager via ServiceLocator
    const { ServiceLocator, Services } = await import('../../utils/ServiceLocator');
    const { WalletManager } = await import('../../managers/WalletManager');
    const wallet = ServiceLocator.get<InstanceType<typeof WalletManager>>(Services.WALLET);

    const walletInfo = await wallet.connectSolana();
    const address = walletInfo.address;
    if (!address) {
      this.isPurchasing = false;
      this.setStatus('Failed to connect wallet');
      this.renderContent();
      return;
    }

    // Step 1: Prepare
    const prepareRes = await fetch(`${API_URL}/purchase/prepare-buy-isd`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ stablecoin, amountUsd: this.amountUsd, walletAddress: address }),
    });

    if (!prepareRes.ok) {
      const err = await prepareRes.json();
      throw new Error(err.error || 'Failed to prepare transaction');
    }

    const prepData = await prepareRes.json();

    // Step 2: Sign
    const signResult = await wallet.signSolanaTransaction(prepData.serializedMessage);
    if (!signResult) throw new Error('Transaction signing cancelled');

    // Step 3: Confirm
    const confirmRes = await fetch(`${API_URL}/purchase/confirm-buy-isd`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({
        purchaseId: prepData.purchaseId,
        signature: signResult.signature,
        publicKey: signResult.publicKey,
      }),
    });

    const confirmData = await confirmRes.json();
    this.isPurchasing = false;

    if (confirmRes.ok && confirmData.success) {
      const isdAmt = confirmData.isdAmount || this.isdEstimate;
      this.setStatus(`${isdAmt.toLocaleString()} $ISD credited!`);
      this.scene.time.delayedCall(2000, () => {
        if (this.scene) this.close();
      });
    } else {
      this.setStatus(confirmData.error || 'Purchase failed');
      this.renderContent();
    }
  }

  /* ================================================================
     HELPERS
     ================================================================ */

  private authHeaders(): HeadersInit {
    const token = (this.monetization as any).getSessionToken?.() || localStorage.getItem('sessionToken') || '';
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  private setStatus(msg: string): void {
    this.statusText.setText(msg);
  }

  private createPresetBtn(
    x: number, y: number, w: number, h: number,
    label: string, active: boolean, onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    const draw = (isActive: boolean, hover: boolean) => {
      bg.clear();
      if (isActive) {
        bg.fillStyle(Colors.cyan, 1);
        bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      } else {
        bg.fillStyle(hover ? 0x2a3a5a : 0x1e2844, 0.8);
        bg.fillRoundedRect(0, 0, w, h, Radius.sm);
        bg.lineStyle(1, hover ? 0x445577 : 0x334466, 0.4);
        bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      }
    };
    draw(active, false);
    btn.add(bg);

    btn.add(this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.xs,
      color: active ? '#000000' : '#99aacc',
      fontFamily: Fonts.heading,
    }).setOrigin(0.5));

    const zone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerover', () => { if (!active) draw(false, true); });
    zone.on('pointerout', () => { draw(active, false); });
    zone.on('pointerdown', onClick);
    btn.add(zone);

    return btn;
  }

  private createActionBtn(
    x: number, y: number, w: number, h: number,
    label: string, bgColor: number, textColor: string,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      bg.fillStyle(hover ? bgColor + 0x111111 : bgColor, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.md);
    };
    draw(false);
    btn.add(bg);

    btn.add(this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.md, color: textColor, fontFamily: Fonts.heading,
    }).setOrigin(0.5));

    const zone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerover', () => { draw(true); this.scene.input.setDefaultCursor('pointer'); });
    zone.on('pointerout', () => { draw(false); this.scene.input.setDefaultCursor('default'); });
    zone.on('pointerdown', onClick);
    btn.add(zone);

    return btn;
  }
}
